import type { ReactNode } from 'react';
import { Empty, Table } from 'antd';
import type { ColumnsType, TableProps } from 'antd/es/table';
import InfiniteScroller from 'components/molecules/InfiniteScroller';
import { CALENDAR_TABLE_HEIGHT_OFFSET } from '../../config/calendarConstants';
import { TPO_METRIC_TABLE } from '../../config/testIds';

export interface MetricTableProps<T extends { id?: string; key?: string; attributeExId?: string; kpi_id?: string }> {
    dataSource: T[];
    columns: ColumnsType<T>;
    isPending: boolean;
    isFetching: boolean;
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    fetchNextPage: () => void;
    handleTableChange: (pagination: unknown, filters: unknown, sorter: any) => void;
    rowKey?: keyof T | ((row: T) => string);
    emptyDescription?: string;
    /** When set, renders a guided empty state (muted panel) instead of the default Empty illustration. */
    emptySubtitle?: string;
    rowSelection?: TableProps<T>['rowSelection'];
    height?: number;
    /** Table id for QA locators. Row ids derive from it plus the resolved row key. */
    testId?: string;
}

function renderEmptyText(description?: string, subtitle?: string, testId?: string, loading?: boolean): ReactNode {
    if (loading) {
        return <div data-testid={testId ? `${testId}_loading` : undefined} />;
    }
    if (subtitle) {
        return (
            <div
                className="flex flex-col items-center justify-center py-12"
                data-testid={testId ? `${testId}_empty` : undefined}
            >
                <p className="text-sm text-gray-500 font-medium">{description ?? 'No data'}</p>
                <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
            </div>
        );
    }
    return (
        <div data-testid={testId ? `${testId}_empty` : undefined}>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={description ?? 'No data'} />
        </div>
    );
}

function MetricTable<T extends { id?: string; key?: string; attributeExId?: string; kpi_id?: string }>({
    dataSource,
    columns,
    isPending,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    handleTableChange,
    rowKey,
    emptyDescription,
    emptySubtitle,
    rowSelection,
    height,
    testId = TPO_METRIC_TABLE,
}: MetricTableProps<T>) {
    const resolveKey: any =
        rowKey ??
        ((row: T) => row.id ?? row.kpi_id ?? row.attributeExId ?? row.key ?? Math.random().toString(36).slice(2));

    const scrollerHeight = height ?? window.innerHeight - CALENDAR_TABLE_HEIGHT_OFFSET;
    const isTableLoading = isPending || isFetching || isFetchingNextPage;

    return (
        <div className="">
            <InfiniteScroller
                data={dataSource}
                onNext={fetchNextPage}
                hasMore={hasNextPage}
                loading={isFetchingNextPage}
                height={scrollerHeight}
                data-testid={`${testId}_scroller`}
            >
                <Table<T>
                    className="planner-metric-table"
                    rowKey={resolveKey}
                    pagination={false}
                    dataSource={dataSource}
                    columns={columns}
                    scroll={{ x: 'max-content' }}
                    loading={isTableLoading}
                    sticky
                    bordered
                    onChange={handleTableChange}
                    rowSelection={rowSelection}
                    data-testid={testId}
                    onRow={(record) =>
                        ({
                            'data-testid': `${testId}_row_${resolveKey(record)}`,
                        } as React.HTMLAttributes<HTMLTableRowElement>)
                    }
                    locale={{
                        emptyText: renderEmptyText(emptyDescription, emptySubtitle, testId, isTableLoading),
                    }}
                />
            </InfiniteScroller>
        </div>
    );
}

export default MetricTable;
